// Retry policy shared by web's and native's React Query clients via the
// `@/shared` symlink. Deliberately dependency-free — no axios, no React — so
// both Vite and Metro accept it unchanged, and so it stays testable as plain
// functions.
//
// React Query inverts control here: it owns the retry loop and asks us two
// questions, "retry?" and "how long?". That is why this is a pair of pure
// functions rather than a retry library — `p-retry` and friends all want to own
// the loop, and none of them can hand back just a delay.

/**
 * Retries permitted *after* the initial attempt — so 3 retries, 4 attempts
 * total. React Query's `failureCount` is 0-based at the point it asks, and it
 * increments only after the decision, so `failureCount < 3` really does allow
 * three retries rather than the two it reads like.
 */
export const MAX_RETRIES = 3;

/**
 * Longest server-requested wait we will sit through before giving up. Our own
 * rate limiter's window is five minutes and it returns the full remainder, so
 * a `Retry-After` well above this is normal rather than hostile.
 */
export const MAX_RETRY_AFTER_MS = 60_000;

/** First retry's backoff ceiling; doubles each attempt up to `MAX_BACKOFF_MS`. */
const BASE_DELAY_MS = 1_000;

/** Ceiling on the exponential term, before the `Retry-After` floor is added. */
const MAX_BACKOFF_MS = 30_000;

/**
 * HTTP status carried by a failed request, or `undefined` when the request never
 * got a response at all (DNS failure, offline, connection reset).
 *
 * Reads the top-level `status` first because `AxiosError` sets one.
 * `response.status` remains as a fallback for anything that only nests it.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) return undefined;

  // A non-numeric `status` (a foreign error carrying `status: "418"`, say) must
  // not be mistaken for an HTTP status.
  const status = (error as { status?: unknown }).status;
  if (typeof status === "number") return status;

  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) return undefined;

  const responseStatus = (response as { status?: unknown }).status;
  return typeof responseStatus === "number" ? responseStatus : undefined;
}

/**
 * Whether the request was deliberately aborted rather than failing. Retrying one
 * of these would burn the budget on a request nobody is waiting for — query
 * functions pass React Query's abort signal, so every navigation cancels some.
 *
 * Matched by `name`/`code` string rather than `instanceof`: Hermes does not
 * reliably expose `DOMException`, and this module runs under Metro too.
 */
export function isCancellation(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;

  const { name, code } = error as { name?: unknown; code?: unknown };
  return name === "CanceledError" || name === "AbortError" || code === "ERR_CANCELED";
}

/**
 * Case-insensitively pull the raw `Retry-After` header off an error's response.
 *
 * Axios exposes headers as an `AxiosHeaders` instance with a case-insensitive
 * `get()`, but a hand-built error may carry a plain object instead, in either
 * casing — so try the getter first and fall back to direct indexing.
 */
function readRetryAfterHeader(error: unknown): unknown {
  if (typeof error !== "object" || error === null) return undefined;

  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) return undefined;

  const headers = (response as { headers?: unknown }).headers;
  if (typeof headers !== "object" || headers === null) return undefined;

  const bag = headers as { get?: unknown; [key: string]: unknown };
  if (typeof bag.get === "function") {
    const viaGetter = (bag.get as (name: string) => unknown)("retry-after");
    if (viaGetter !== undefined && viaGetter !== null) return viaGetter;
  }

  return bag["retry-after"] ?? bag["Retry-After"];
}

/**
 * The server's requested wait in milliseconds, or `0` when it did not ask for one.
 *
 * Anything not strictly valid returns `0` rather than propagating a `NaN` into
 * the delay arithmetic, where it would poison every downstream comparison.
 */
export function getRetryAfterMs(error: unknown): number {
  const raw = readRetryAfterHeader(error);
  if (typeof raw !== "string") return 0;

  const value = raw.trim();
  if (value === "") return 0;

  // Digits only, per RFC 9110's delta-seconds. `Number()` would happily accept
  // "5.5", "1e3" and "-5", none of which are a valid delta-seconds.
  if (/^\d+$/.test(value)) {
    const seconds = Number(value);
    return Number.isSafeInteger(seconds) ? seconds * 1_000 : 0;
  }

  // Only an HTTP-date can be left, and every form of one contains letters (a day
  // name, a month name, "GMT"). Gating on that keeps `Date.parse`'s leniency
  // from reinterpreting a malformed numeric like "-5" as a year.
  if (!/[a-z]/i.test(value)) return 0;

  const parsedMs = Date.parse(value);
  if (Number.isNaN(parsedMs)) return 0;

  // A date already in the past means "retry now", not "retry in the past".
  return Math.max(0, parsedMs - Date.now());
}

/**
 * Whether React Query should retry a failed request.
 *
 * Retries transport failures, 5xx and 429; never retries other 4xx responses,
 * since the server has decided and repeating the request cannot change its
 * mind. This keeps a transient infrastructure failure from being treated as a
 * session verdict.
 */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (isCancellation(error)) return false;
  if (failureCount >= MAX_RETRIES) return false;

  const status = getErrorStatus(error);
  // No status means the request never reached the server — worth another go.
  if (status !== undefined && status < 500 && status !== 429) return false;

  // A query waiting longer than the patience budget is less useful than a
  // prompt failure that the UI can surface and let the user revisit.
  if (getRetryAfterMs(error) > MAX_RETRY_AFTER_MS) return false;

  return true;
}

/**
 * How long to wait before the next attempt: the server's `Retry-After` floor
 * plus full jitter over the current exponential window.
 *
 * The jitter is applied last and spans the whole window (AWS's "full jitter"),
 * rather than the tempting `max(retryAfter, jitter)` — that variant collapses
 * every client onto exactly the server's floor whenever the floor exceeds the
 * current backoff, manufacturing the synchronized herd the jitter exists to
 * break up.
 *
 * `random` is injectable so the jitter can be asserted without stubbing a global.
 */
export function getRetryDelayMs(
  failureCount: number,
  error: unknown,
  random: () => number = Math.random,
): number {
  const backoffCeilingMs = Math.min(MAX_BACKOFF_MS, BASE_DELAY_MS * 2 ** failureCount);
  return getRetryAfterMs(error) + random() * backoffCeilingMs;
}
